/**
 * Graph Options Generation Utility
 *
 * Validates and generates ECharts options from PlatinumData and GraphConfig.
 * This utility is used by both PlatinumGraphScreenshot and Tables.tsx to
 * validate graphs before rendering.
 */

import type { PlatinumData } from '../../../types/carbon/models/PlatinumData'
import type { GraphConfig } from '../types'
import { convertPlatinumToTable, transposeParsedTable } from './convertPlatinumData'
import { generators } from '../generators'
import { GeneratorError } from '../generators/helpers'

export interface GraphOptionsSuccess {
  success: true;
  chartOptions: any;
}

export interface GraphOptionsError {
  success: false;
  error: string;
}

export type GraphOptionsResult = GraphOptionsSuccess | GraphOptionsError;

/**
 * Attempts to generate chart options from PlatinumData and GraphConfig
 *
 * @param data - The PlatinumData from the backend
 * @param config - The GraphConfig specifying chart type, selections, etc.
 * @returns GraphOptionsResult - Either success with chartOptions or error with message
 */
export function generateGraphOptions(
  data: PlatinumData,
  config: GraphConfig
): GraphOptionsResult {
  try {
    console.log('generateGraphOptions - Received config:', JSON.stringify(config, null, 2))

    // Convert Platinum data to table format
    let parsedData = convertPlatinumToTable(data, config.dataType)
    if (!parsedData) {
      return {
        success: false,
        error: 'Failed to convert Platinum table data.',
      }
    }

    console.log('generateGraphOptions - Parsed table headers:', parsedData.headers)
    console.log('generateGraphOptions - Parsed table row count:', parsedData.rowCnt)
    console.log('generateGraphOptions - Parsed table col count:', parsedData.colCnt)

    // Transpose if needed
    if (config.switchAxis) {
      parsedData = transposeParsedTable(parsedData)
      console.log('generateGraphOptions - After transpose, headers:', parsedData.headers)
    }

    // Generate chart options
    const generator = generators[config.chartType]
    if (!generator) {
      return {
        success: false,
        error: `No generator found for chart type: ${config.chartType}`,
      }
    }

    const settings = {
      dataType: config.dataType,
      selectedHeader: config.selectedColumn,
      selectedRows: config.switchAxis ? config.selectedColumns : config.selectedRows,
      selectedColumns: config.switchAxis ? config.selectedRows : config.selectedColumns,
      // #1201 — same opt-in the live component honours, so a screenshot of a
      // chart matches the chart.
      showDataLabels: config.showDataLabels,
      // #1808 — bar order, when the config carries one.
      sortOrder: config.sortOrder,
    }

    console.log('generateGraphOptions - Generator settings:', JSON.stringify(settings, null, 2))

    const chartOptions = generator(parsedData, '', settings)
    if (!chartOptions) {
      return {
        success: false,
        error: `Generator failed for ${config.chartType}.`,
      }
    }

    return {
      success: true,
      chartOptions,
    }
  } catch (error: any) {
    const errorMessage =
      error instanceof GeneratorError ? error.message : `Error generating chart: ${error.message}`
    return {
      success: false,
      error: errorMessage,
    }
  }
}
